import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { impersonationService } from '../services/impersonation.service';

interface ImpersonationContextType {
  isImpersonating: boolean;
  impersonatedCompanyName: string | null;
  impersonatedCompanyId: number | null;
  originalCompanyId: number | null;
  startImpersonation: (tenantId: number, tenantName: string) => Promise<void>;
  exitImpersonation: () => Promise<void>;
  isLoading: boolean;
  error: string | null;
}

const ImpersonationContext = createContext<ImpersonationContextType | undefined>(undefined);

interface ImpersonationProviderProps {
  children: ReactNode;
}

export const ImpersonationProvider: React.FC<ImpersonationProviderProps> = ({ children }) => {
  const [isImpersonating, setIsImpersonating] = useState<boolean>(false);
  const [impersonatedCompanyName, setImpersonatedCompanyName] = useState<string | null>(null);
  const [impersonatedCompanyId, setImpersonatedCompanyId] = useState<number | null>(null);
  const [originalCompanyId, setOriginalCompanyId] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Check impersonation status on mount
  useEffect(() => {
    const data = impersonationService.getImpersonationData();
    if (data) {
      setIsImpersonating(true);
      setImpersonatedCompanyName(data.impersonatedCompanyName);
      setImpersonatedCompanyId(data.impersonatedCompanyId);
      setOriginalCompanyId(data.originalCompanyId);
    }
  }, []);

  const startImpersonation = useCallback(async (tenantId: number, tenantName: string): Promise<void> => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await impersonationService.startImpersonation(tenantId);

      if (response.success) {
        setIsImpersonating(true);
        setImpersonatedCompanyName(response.impersonated_company_name || tenantName);
        setImpersonatedCompanyId(response.impersonated_company_id);
        setOriginalCompanyId(response.original_company_id);
        
        // Reload the page to refresh all data with new token
        window.location.reload();
      } else {
        throw new Error('Failed to start impersonation');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to start impersonation';
      setError(errorMessage);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const exitImpersonation = useCallback(async (): Promise<void> => {
    try {
      setIsLoading(true);
      setError(null);

      await impersonationService.exitImpersonation();

      setIsImpersonating(false);
      setImpersonatedCompanyName(null);
      setImpersonatedCompanyId(null);
      setOriginalCompanyId(null);

      // Reload the page to refresh all data with original token
      window.location.reload();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to exit impersonation';
      setError(errorMessage);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const value: ImpersonationContextType = {
    isImpersonating,
    impersonatedCompanyName,
    impersonatedCompanyId,
    originalCompanyId,
    startImpersonation,
    exitImpersonation,
    isLoading,
    error,
  };

  return (
    <ImpersonationContext.Provider value={value}>
      {children}
    </ImpersonationContext.Provider>
  );
};

export const useImpersonation = (): ImpersonationContextType => {
  const context = useContext(ImpersonationContext);
  if (context === undefined) {
    throw new Error('useImpersonation must be used within an ImpersonationProvider');
  }
  return context;
};
